// Meta WhatsApp Embedded Signup (Coexistence) launcher.
//
// Flow: load the Facebook JS SDK once, then FB.login() with our WhatsApp ES
// configuration. The popup walks the owner through connecting their existing
// WhatsApp Business app (Coexistence, they keep using WhatsApp on their phone)
// and returns:
//   - an authorization `code` via the FB.login callback (response_type=code)
//   - { phone_number_id, waba_id } via a window 'message' event the SDK posts
//
// We pair the two and hand them to the meta-whatsapp-onboard edge function,
// which exchanges the code for a token server-side. The token never touches
// the browser.
//
// Requires VITE_FB_APP_ID + VITE_META_ES_CONFIG_ID (the ES configuration id
// created in the Meta app dashboard with the Coexistence feature enabled).

const SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";
const GRAPH_VERSION = "v25.0";
// Backstop for the FB.login callback never firing (e.g. popup killed by the
// browser). Generous: the user may legitimately spend minutes in the wizard.
const LOGIN_TIMEOUT_MS = 5 * 60 * 1000;
// Meta's Embedded Signup auth code carries a documented ~30 SECOND
// time-to-live, so every millisecond spent here eats the server's budget to
// exchange it. In the normal flow the session-info message lands before the
// FB.login callback, so we consume it with zero wait; this grace window only
// covers a near-simultaneous arrival. On re-connects Meta skips the wizard and
// never posts session info at all. We must not stall there, since the edge
// function derives the WABA from the token anyway.
// See TTL note: https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/implementation
export const SESSION_INFO_GRACE_MS = 1000;
/** Hard ceiling for the grace window. Anything near the 30s code TTL risks
 *  expiring the code before the server can exchange it. */
export const CODE_TTL_BUDGET_MS = 30 * 1000;

export interface EmbeddedSignupResult {
  code: string;
  /** Date.now() when FB.login handed us the code. The code's ~30s TTL starts
   *  around here, so the caller reports the elapsed time to the server to make
   *  an expiry failure diagnosable instead of a guess. */
  codeReceivedAt: number;
  phoneNumberId: string | null;
  wabaId: string | null;
}

interface EmbeddedSignupSession {
  phoneNumberId: string | null;
  wabaId: string | null;
}

export type EmbeddedSignupSessionEvent =
  | { kind: "finished"; session: EmbeddedSignupSession }
  | { kind: "cancelled"; currentStep: string | null }
  | { kind: "error"; message: string }
  | null;

interface FBLoginResponse {
  authResponse?: { code?: string } | null;
  status?: string;
}

declare global {
  interface Window {
    FB?: {
      init: (opts: Record<string, unknown>) => void;
      login: (cb: (resp: FBLoginResponse) => void, opts: Record<string, unknown>) => void;
    };
    fbAsyncInit?: () => void;
  }
}

let sdkPromise: Promise<void> | null = null;

/** Load + init the Facebook JS SDK exactly once. */
function loadSdk(appId: string): Promise<void> {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<void>((resolve, reject) => {
    if (window.FB) {
      resolve();
      return;
    }
    window.fbAsyncInit = () => {
      window.FB!.init({
        appId,
        autoLogAppEvents: true,
        xfbml: false,
        version: GRAPH_VERSION,
      });
      resolve();
    };
    const existing = document.getElementById("facebook-jssdk");
    if (existing) return; // fbAsyncInit will still fire
    const script = document.createElement("script");
    script.id = "facebook-jssdk";
    script.src = SDK_SRC;
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.onerror = () => reject(new Error("Failed to load the Facebook SDK"));
    document.body.appendChild(script);
  });
  return sdkPromise;
}

export function embeddedSignupConfigured(): boolean {
  return Boolean(import.meta.env.VITE_FB_APP_ID && import.meta.env.VITE_META_ES_CONFIG_ID);
}

/**
 * Meta Embedded Signup is available whenever its app/config IDs are present.
 * VITE_WHATSAPP_META_ENABLED=false remains an emergency kill switch, but an
 * absent flag no longer hides an approved, fully configured integration.
 */
export function metaWhatsAppEnabled(): boolean {
  return resolveMetaWhatsAppEnabled(import.meta.env.VITE_WHATSAPP_META_ENABLED);
}

export function resolveMetaWhatsAppEnabled(value: string | undefined): boolean {
  return value?.trim().toLowerCase() !== "false";
}

export function isAllowedMetaOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    return (
      url.protocol === "https:" &&
      (url.hostname === "facebook.com" || url.hostname.endsWith(".facebook.com"))
    );
  } catch {
    return false;
  }
}

/** Parse the session-info postMessage emitted by WhatsApp Embedded Signup. */
export function parseEmbeddedSignupEvent(data: unknown): EmbeddedSignupSessionEvent {
  let parsed: unknown = data;
  if (typeof data === "string") {
    try {
      parsed = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object") return null;
  const payload = parsed as Record<string, unknown>;
  if (payload.type !== "WA_EMBEDDED_SIGNUP") return null;

  const event = typeof payload.event === "string" ? payload.event : "";
  const eventData = payload.data && typeof payload.data === "object"
    ? payload.data as Record<string, unknown>
    : {};
  const finishedEvents = new Set([
    "FINISH",
    "FINISH_ONLY_WABA",
    "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
  ]);

  if (finishedEvents.has(event)) {
    return {
      kind: "finished",
      session: {
        phoneNumberId:
          typeof eventData.phone_number_id === "string" ? eventData.phone_number_id : null,
        wabaId: typeof eventData.waba_id === "string" ? eventData.waba_id : null,
      },
    };
  }
  if (event === "ERROR") {
    return {
      kind: "error",
      message:
        typeof eventData.error_message === "string"
          ? eventData.error_message
          : "Meta couldn't complete WhatsApp signup.",
    };
  }
  if (event === "CANCEL") {
    return {
      kind: "cancelled",
      currentStep: typeof eventData.current_step === "string" ? eventData.current_step : null,
    };
  }
  return null;
}

/**
 * Launch the Coexistence Embedded Signup popup. Resolves with the auth code +
 * the phone_number_id / waba_id captured from the SDK message event. Rejects if
 * the user cancels or the SDK can't load.
 */
export async function launchWhatsAppEmbeddedSignup(): Promise<EmbeddedSignupResult> {
  const appId = import.meta.env.VITE_FB_APP_ID as string | undefined;
  const configId = import.meta.env.VITE_META_ES_CONFIG_ID as string | undefined;
  if (!appId || !configId) {
    throw new Error("WhatsApp signup isn't configured yet (missing VITE_FB_APP_ID / VITE_META_ES_CONFIG_ID).");
  }

  await loadSdk(appId);

  let resolveSession!: (session: EmbeddedSignupSession) => void;
  let rejectSession!: (error: Error) => void;
  const sessionPromise = new Promise<EmbeddedSignupSession>((resolve, reject) => {
    resolveSession = resolve;
    rejectSession = reject;
  });

  // Captured synchronously so that once the code arrives we can use an
  // already-delivered session with zero delay. The code's ~30s TTL leaves no
  // room to await a promise we have already satisfied.
  let receivedSession: EmbeddedSignupSession | null = null;

  const onMessage = (event: MessageEvent) => {
    if (!isAllowedMetaOrigin(event.origin)) return;
    const parsed = parseEmbeddedSignupEvent(event.data);
    if (parsed?.kind === "finished") {
      receivedSession = parsed.session;
      resolveSession(parsed.session);
    } else if (parsed?.kind === "cancelled") {
      rejectSession(
        new Error(
          parsed.currentStep
            ? `Signup was cancelled at ${parsed.currentStep}.`
            : "Signup was cancelled before completing.",
        ),
      );
    } else if (parsed?.kind === "error") {
      rejectSession(new Error(parsed.message));
    }
  };

  // The session-info message may reject (CANCEL/ERROR) after the code path has
  // already thrown; observe it so that never surfaces as an unhandled rejection.
  sessionPromise.catch(() => {});

  window.addEventListener("message", onMessage);
  let loginTimeoutId: number | undefined;
  let graceTimeoutId: number | undefined;

  try {
    const codePromise = new Promise<string>((resolve, reject) => {
      window.FB!.login(
        (resp) => {
          const c = resp?.authResponse?.code;
          if (c) resolve(c);
          else reject(new Error("Signup was cancelled before completing."));
        },
        {
          config_id: configId,
          response_type: "code",
          override_default_response_type: true,
          // Coexistence: connect the owner's existing WhatsApp Business app
          // instead of provisioning a brand-new dedicated number.
          extras: {
            setup: {},
            featureType: "whatsapp_business_app_onboarding",
            sessionInfoVersion: "3",
          },
        },
      );
    });

    const loginTimeout = new Promise<never>((_, reject) => {
      loginTimeoutId = window.setTimeout(
        () => reject(new Error("WhatsApp signup timed out. Please try again.")),
        LOGIN_TIMEOUT_MS,
      );
    });
    const code = await Promise.race([codePromise, loginTimeout]);
    const codeReceivedAt = Date.now();

    // The code is now ticking against its ~30s TTL, so spend as little time
    // here as possible. Normally the FINISH message has already landed and we
    // continue immediately. Otherwise wait only briefly for a near-simultaneous
    // arrival, then proceed with null ids. On a re-connect Meta skips the
    // wizard and never sends session info, and meta-whatsapp-onboard resolves
    // the WABA from the token's granular scopes regardless.
    const session: EmbeddedSignupSession = receivedSession ??
      (await Promise.race([
        sessionPromise,
        new Promise<EmbeddedSignupSession>((resolve) => {
          graceTimeoutId = window.setTimeout(
            () => resolve({ phoneNumberId: null, wabaId: null }),
            SESSION_INFO_GRACE_MS,
          );
        }),
      ]));
    return {
      code,
      codeReceivedAt,
      phoneNumberId: session.phoneNumberId,
      wabaId: session.wabaId,
    };
  } finally {
    if (loginTimeoutId !== undefined) window.clearTimeout(loginTimeoutId);
    if (graceTimeoutId !== undefined) window.clearTimeout(graceTimeoutId);
    window.removeEventListener("message", onMessage);
  }
}
